import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-deep-charcoal">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg tracking-tight">fans<span className="text-electric-cyan">.audio</span></span>
          </a>
          <a href="/" className="flex items-center gap-2 text-sm text-text-secondary hover:text-electric-cyan transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back
          </a>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-3xl mx-auto"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="mb-12">
            <span className="terminal-label text-xs">legal::terms-of-service</span>
            <h1 className="text-4xl sm:text-5xl font-bold mt-4 mb-4">
              Terms of <span className="text-electric-cyan">Service</span>
            </h1>
            <p className="text-text-muted text-sm">Last Updated: May 2026</p>
          </motion.div>

          {/* Section 1 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">01.</span> The Agreement
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <p className="text-text-secondary leading-relaxed">
                fans.audio is a trading style of Boogie Microphone Limited ("the Company"). By accessing the fans.audio platform ("the Platform"), whether as an Artist or a Fan (collectively "Users"), you enter into a legally binding agreement with Boogie Microphone Limited.
              </p>
            </div>
          </motion.section>

          {/* Section 2 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">02.</span> Artist Sovereignty & Content Verification
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <ul className="space-y-4 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Ownership:</strong> Artists retain 100% ownership of all Intellectual Property uploaded to the Platform. The Company claims no ownership or licensing rights beyond the limited right to host, process, and deliver the content to authorized Fans.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">The Authentic Signal:</strong> To protect the integrity of the Platform, the Company uses active intelligent processing of all tracks to flag and filter for unauthorized AI-generated content or copyright infringement. By uploading, the Artist warrants the work is an authentic creation.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Fixed Pressings:</strong> Pressings represent collectible digital releases. Once an Artist locks and publishes a Pressing, the contents become immutable to ensure the long-term value of the Fan's collection.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 3 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">03.</span> The Credit Economy & Access Keys
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <ul className="space-y-4 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Subscription Model:</strong> Fans subscribe to the Platform for the right to use credits to unlock Artist Pressings.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">The Access Key:</strong> Once a Pressing is unlocked using a credit, the Fan is granted a digital "key" to that Pressing. This key remains valid for the remaining lifetime of the Fan's active subscription.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Platform Evolution:</strong> Users acknowledge that the Platform is in an active state of growth. Specific subscription tiers, credit values, and feature sets are subject to refinement as the ecosystem scales.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 4 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">04.</span> Free Accounts & Branding
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <ul className="space-y-4 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Free Tier Usage:</strong> The Platform is free for Artists to join and use for the purpose of building their Fan Circle and initiating Transmissions.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Platform Attribution:</strong> In exchange for the use of the free tools, Artists agree that all electronic Transmissions sent via the Platform will include fans.audio branding and/or non-intrusive advertising provided by the Company in the footer or margins.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 5 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">05.</span> Account Closure & Data Persistence
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <ul className="space-y-4 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Artist Exit Strategy:</strong> Should an Artist choose to close their account, their public profile and public-facing Pressings will be removed from the Platform's general discovery areas.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Legacy Access:</strong> To protect the Fan's investment, any Pressings previously unlocked by Fans will remain available to those specific Fans via their private collection. The Artist's content will persist on the Platform's servers solely to fulfill these existing "Access Key" obligations.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Relationship Portability:</strong> Per our Sovereign Data policy, the Company will facilitate the export of the Artist's Fan contact data upon account closure to ensure the Artist maintains their direct audience relationship.</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Section 6 */}
          <motion.section variants={fadeInUp} className="mb-10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-electric-cyan font-mono text-sm">06.</span> Legal Contact & Compliance
            </h2>
            <div className="glass-card p-6 rounded-xl border border-white/5">
              <ul className="space-y-3 text-text-secondary text-sm">
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Governing Law:</strong> This agreement is governed by the laws of England and Wales.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Official Notices:</strong> All legal notices, copyright claims, or data requests must be directed to: admin@bmic.music.</span>
                </li>
                <li className="flex gap-2">
                  <span className="text-electric-cyan font-mono">›</span>
                  <span><strong className="text-white">Physical Address:</strong> 4 Yorke Street, Hucknall, Nottingham, England, NG15 7BT</span>
                </li>
              </ul>
            </div>
          </motion.section>

          {/* Footer note */}
          <motion.div variants={fadeInUp} className="mt-12 pt-8 border-t border-white/5 text-center">
            <p className="text-text-muted text-xs font-mono">
              &gt; fans.audio — a trading style of Boogie Microphone Limited
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
